import React, {useState, useEffect, useMemo, useRef} from 'react';
import {
  Box,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  TextField,
  MenuItem,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import {
  AddNewFieldSoilData,
  ISoilData,
} from 'screens/Dashboard/Customers/CustomerDetail/CustomerDashboardMapping/components/AddNewFieldSoilData';
import {IAddNewDataPointFormProps} from './AddNewDataPointForm.types';
import {EDataPointType} from './AddNewDataPointForm.constants';
import {GeneralNoteForm} from './GeneralNoteForm';
import {IssueLogForm} from './IssueLogForm';
import {GrowthObservationForm} from './GrowthObservationForm';
import {ImageUploadModal} from './ImageUploadModal';
import {DataPointTypeSelector} from './DataPointTypeSelector';
import {CustomerSupportForm} from './CustomerSupportForm';
import {
  IGrowthObservationData,
  IManagementLogData,
  IHarvestSummaryData,
  IPostHarvestReviewData,
  ICustomerSupportData,
} from './AddNewDataPointForm.types';
import {ManagementLogForm} from './ManagementLogForm';
import {HarvestSummaryForm} from './HarvestSummaryForm';
import {PostHarvestReviewForm} from './PostHarvestReviewForm';
import {
  apiCreateFarmNote,
  apiCreateIssueLog,
  apiCreateSoilAssessment,
  apiCreateGrowthObservation,
  apiUpdateIssueLog,
  apiGetLatestSoilAssessment,
  apiGetLatestIssueLog,
  apiGetLatestGrowthObservation,
  apiCreateManagementLog,
  apiCreateHarvestSummary,
  apiCreatePostHarvestReview,
  apiGetPestDiseaseOptions,
} from 'constants/api/fieldData';
import {apiUploadFile} from 'constants/api/fields';
import {IPolygonData} from 'constants/interfaces';

function formatFarmFieldOptionLabel(f: IPolygonData): string {
  const name = f.info?.fieldName?.trim() || 'Field';
  const crop = f.info?.produce?.name || '';
  const ha = f.info?.hectares;
  const haPart =
    ha !== undefined && ha !== null && String(ha).trim() !== ''
      ? `${ha} Ha`
      : '';
  if (crop && haPart) return `${name} | ${crop} ${haPart}`;
  if (crop) return `${name} | ${crop}`;
  if (haPart) return `${name} ${haPart}`;
  return name;
}

export const AddNewDataPointForm: React.FC<IAddNewDataPointFormProps> = ({
  field,
  fields: fieldsProp,
  farmId,
  onClose,
  onSuccess,
  onChooseOnMap,
  pickedLocation,
}) => {
  const defaultPestDiseaseOptions: Array<{id: string; name: string}> = [
    {id: 'fallback-aphids', name: 'Aphids'},
    {id: 'fallback-thrips', name: 'Thrips'},
    {id: 'fallback-whitefly', name: 'Whitefly'},
    {id: 'fallback-armyworm', name: 'Fall armyworm'},
    {id: 'fallback-cutworm', name: 'Cutworm'},
    {id: 'fallback-leafminer', name: 'Leaf miner'},
    {id: 'fallback-rust', name: 'Rust'},
    {id: 'fallback-powdery-mildew', name: 'Powdery mildew'},
    {id: 'fallback-downy-mildew', name: 'Downy mildew'},
    {id: 'fallback-blight', name: 'Blight'},
  ];

  const growthObservationStageIdMap: Record<string, number> = {
    'Pre-plant': 1,
    Plant: 2,
    'Post planting': 3,
    Germination: 4,
    'Vegetative growth': 5,
    'Fruit set / Bulking': 6,
    'Pre Harvest': 7,
  };

  const [dataPointType, setDataPointType] = useState<EDataPointType | ''>('');
  const [location, setLocation] = useState('');

  useEffect(() => {
    if (pickedLocation) {
      setLocation(pickedLocation);
    }
  }, [pickedLocation]);
  const [noteTitle, setNoteTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [soilData, setSoilData] = useState<ISoilData | null>(null);
  const [existingIssueLogId, setExistingIssueLogId] = useState<number | null>(
    null,
  );
  // Issue Log
  const [issueLogDate, setIssueLogDate] = useState(
    () => new Date().toISOString().split('T')[0],
  );
  const [issueLogType, setIssueLogType] = useState('');
  const [issueLogPestsAndDiseasesId, setIssueLogPestsAndDiseasesId] =
    useState('');
  const [pestDiseaseOptions, setPestDiseaseOptions] = useState<
    Array<{id: string; name: string}>
  >([]);
  const [pestDiseaseOptionsLoading, setPestDiseaseOptionsLoading] =
    useState(false);
  const [pestDiseaseOptionsError, setPestDiseaseOptionsError] = useState('');
  const [issueLogSeverityScale, setIssueLogSeverityScale] = useState('');
  const [issueLogControlMeasures, setIssueLogControlMeasures] = useState('');
  const [issueLogPreventativeMeasures, setIssueLogPreventativeMeasures] =
    useState('');
  const [issueLogDuringHarvest, setIssueLogDuringHarvest] = useState(false);

  // Growth Observation
  const [growthObservationStage, setGrowthObservationStage] =
    useState<string>('');
  const [growthObservationData, setGrowthObservationData] =
    useState<IGrowthObservationData>({
      observationDate: new Date().toISOString().split('T')[0],
    });
  // Management Log
  const [managementLogData, setManagementLogData] =
    useState<IManagementLogData>({
      logDate: new Date().toISOString().split('T')[0],
      activityType: '',
    });

  // Harvest Summary
  const [harvestSummaryData, setHarvestSummaryData] =
    useState<IHarvestSummaryData>({});

  // Post Harvest Review
  const [postHarvestReviewData, setPostHarvestReviewData] =
    useState<IPostHarvestReviewData>({});

  // Customer Support
  const [customerSupportData, setCustomerSupportData] =
    useState<ICustomerSupportData>({
      subject: '',
      description: '',
      priority: 'medium',
    });

  const baseFields = useMemo(() => {
    return fieldsProp?.length ? fieldsProp : field ? [field] : [];
  }, [fieldsProp, field]);

  const fieldOptions = useMemo(() => {
    const options = [...baseFields];
    const hasGeneral = options.some((f) => String(f.id) === 'GENERAL');
    if (!hasGeneral) {
      options.unshift({
        id: 'GENERAL',
        info: {fieldName: 'General Farm'},
      } as IPolygonData);
    }
    return options;
  }, [baseFields]);

  const [selectedFieldId, setSelectedFieldId] = useState(() => {
    if (field?.id) return String(field.id);
    return 'GENERAL';
  });

  useEffect(() => {
    if (field?.id) {
      setSelectedFieldId(String(field.id));
    } else {
      setSelectedFieldId('GENERAL');
    }
  }, [field?.id]);

  const selectedField = useMemo((): IPolygonData => {
    const match = fieldOptions.find(
      (f) => String(f.id ?? '') === selectedFieldId,
    );
    return match ?? (field && field.id ? field : fieldOptions[0]);
  }, [fieldOptions, selectedFieldId, field]);

  const farmFieldId =
    selectedFieldId === 'GENERAL' ? null : String(selectedField?.id ?? '');
  const prevFarmFieldIdRef = useRef<string>('');

  useEffect(() => {
    const fetchExistingData = async () => {
      if (farmId && farmFieldId) {
        const prevId = prevFarmFieldIdRef.current;
        const fieldJustChanged = prevId !== '' && prevId !== farmFieldId;
        prevFarmFieldIdRef.current = farmFieldId;

        // Clear previous data before fetching new ones
        setSoilData(null);
        setExistingIssueLogId(null);
        setIssueLogDate(new Date().toISOString().split('T')[0]);
        setIssueLogType('');
        setIssueLogPestsAndDiseasesId('');
        setIssueLogSeverityScale('');
        setIssueLogControlMeasures('');
        setIssueLogPreventativeMeasures('');
        setIssueLogDuringHarvest(false);
        setGrowthObservationStage('');
        setGrowthObservationData({
          observationDate: new Date().toISOString().split('T')[0],
        });
        setManagementLogData({
          logDate: new Date().toISOString().split('T')[0],
          activityType: '',
        });
        setHarvestSummaryData({});
        setPostHarvestReviewData({});

        if (fieldJustChanged) {
          setNoteTitle('');
          setNotes('');
          setUploadedImages([]);
          setLocation('');
        }

        try {
          // Fetch and populate soil assessment data
          const soilAssessment = await apiGetLatestSoilAssessment(farmFieldId);
          if (soilAssessment) {
            setSoilData({
              soilType: soilAssessment.soilType || '',
              pH: soilAssessment.pH,
              P: soilAssessment.P,
              K: soilAssessment.K,
              Ca: soilAssessment.Ca,
              Mg: soilAssessment.Mg,
              Na: soilAssessment.Na,
              cations: soilAssessment.totalCations,
              exchangeableAcidity: soilAssessment.exchangeableAcidity,
              anions: soilAssessment.anions,
              Zn: soilAssessment.Zn,
              Mn: soilAssessment.Mn,
              Cu: soilAssessment.Cu,
            });
          }

          // Fetch and populate issue log data
          const issueLog = await apiGetLatestIssueLog(farmFieldId);
          if (issueLog) {
            setExistingIssueLogId(Number(issueLog.id));
            if (issueLog.issueDate) setIssueLogDate(issueLog.issueDate);
            if (issueLog.issueType) setIssueLogType(issueLog.issueType);
            if (issueLog.severityScale)
              setIssueLogSeverityScale(String(issueLog.severityScale));
            if (issueLog.controlMeasuresApplied)
              setIssueLogControlMeasures(issueLog.controlMeasuresApplied);
            if (issueLog.preventativeMeasuresTaken)
              setIssueLogPreventativeMeasures(
                issueLog.preventativeMeasuresTaken,
              );
            if (issueLog.issueDuringHarvest !== undefined)
              setIssueLogDuringHarvest(issueLog.issueDuringHarvest);
          }

          // Fetch and populate growth observation data
          const growthObservation = await apiGetLatestGrowthObservation(
            farmFieldId,
          );
          if (growthObservation) {
            setGrowthObservationStage(growthObservation.stage || '');
            setGrowthObservationData({
              observationDate: growthObservation.observationDate || '',
              latestAcceptablePlantingDate:
                growthObservation.latestAcceptablePlantingDate || '',
              expectedGerminationRate:
                growthObservation.expectedGerminationRate || '',
              estimatedYield: growthObservation.estimatedYield || '',
              plannedHa: growthObservation.plannedHa || '',
              haPrepared: growthObservation.haPrepared || '',
              rowSpacing: growthObservation.rowSpacing || '',
              plantedPct: growthObservation.plantedPct || '',
              plantHeightCm: growthObservation.plantHeightCm || '',
              germinationRatePct: growthObservation.germinationRatePct || '',
              seedlingVigorScale: growthObservation.seedlingVigorScale || '',
              rootLengthScale: growthObservation.rootLengthScale || '',
              cropMaturityAssessment:
                growthObservation.cropMaturityAssessment || '',
              uniformityOfMaturity:
                growthObservation.uniformityOfMaturity || '',
              leavesPerPlant: growthObservation.leavesPerPlant || '',
              floweringUniformityScale:
                growthObservation.floweringUniformityScale || '',
              marketReady: growthObservation.marketReady,
              marketReadyComment: growthObservation.marketReadyComment || '',
              tonsHedged: growthObservation.tonsHedged || '',
              fruitSetPercent: growthObservation.fruitSetPercent || '',
              fruitSetUniformityScale:
                growthObservation.fruitSetUniformityScale || '',
              fruitAvgLengthCm: growthObservation.fruitAvgLengthCm || '',
              fruitAvgDiameterCm: growthObservation.fruitAvgDiameterCm || '',
              pctCropReadyForHarvest:
                growthObservation.pctCropReadyForHarvest || '',
              estimatedHarvestDate:
                growthObservation.estimatedHarvestDate || '',
              logisticsReady: growthObservation.logisticsReady,
              comment: growthObservation.comment || '',
            });
          }
        } catch (error) {
          console.error('Failed to fetch data:', error);
        }
      } else {
        // Clear data when general farm is selected
        setSoilData(null);
        setExistingIssueLogId(null);
        setIssueLogDate('');
        setIssueLogType('');
        setIssueLogPestsAndDiseasesId('');
        setIssueLogSeverityScale('');
        setIssueLogControlMeasures('');
        setIssueLogPreventativeMeasures('');
        setIssueLogDuringHarvest(false);
        setGrowthObservationStage('');
        setGrowthObservationData({
          observationDate: new Date().toISOString().split('T')[0],
        });
        setManagementLogData({
          logDate: new Date().toISOString().split('T')[0],
          activityType: '',
        });
        setHarvestSummaryData({});
        setPostHarvestReviewData({});
      }
    };
    fetchExistingData();
  }, [farmId, farmFieldId]);

  useEffect(() => {
    const loadPestDiseaseOptions = async () => {
      try {
        setPestDiseaseOptionsLoading(true);
        setPestDiseaseOptionsError('');
        const options = await apiGetPestDiseaseOptions();
        if (Array.isArray(options) && options.length > 0) {
          setPestDiseaseOptions(options);
        } else {
          // Keep Issue Log usable if backend list endpoint is unavailable/empty.
          setPestDiseaseOptions(defaultPestDiseaseOptions);
        }
      } catch (error) {
        console.error('Failed to fetch pest/disease options:', error);
        setPestDiseaseOptions(defaultPestDiseaseOptions);
        setPestDiseaseOptionsError('');
      }
      setPestDiseaseOptionsLoading(false);
    };
    loadPestDiseaseOptions();
  }, []);

  const canSaveGeneralNotes =
    dataPointType === 'General Notes' &&
    noteTitle.trim().length > 0 &&
    notes.trim().length > 0;

  const canSaveIssueLog =
    dataPointType === 'Issue Log' &&
    issueLogDate.trim().length > 0 &&
    issueLogType.trim().length > 0 &&
    issueLogPestsAndDiseasesId.trim().length > 0;

  const canSaveCustomerSupport =
    dataPointType === EDataPointType.CUSTOMER_SUPPORT &&
    customerSupportData.subject.trim().length > 0 &&
    customerSupportData.description.trim().length > 0;

  const [isLoading, setIsLoading] = useState(false);

  const uploadNoteImages = async (): Promise<string[]> => {
    if (!uploadedImages.length) return [];

    const timestamp = new Date().getTime();
    const urls = await Promise.all(
      uploadedImages.map((image, index) => {
        const fileExtension = image.name.split('.').pop()?.substr(0, 4);
        const fileKey = `${timestamp}_${farmFieldId ?? farmId}_field_note_${
          index + 1
        }.${fileExtension || 'jpg'}`;
        return apiUploadFile(image, fileKey);
      }),
    );

    return urls.filter(
      (url) => typeof url === 'string' && url.trim().length > 0,
    );
  };

  const buildCommentWithImageUrls = (
    comment: string,
    imageUrls: string[],
  ): string => {
    const baseComment = comment.trim();
    if (!imageUrls.length) {
      return baseComment;
    }

    const imageLines = imageUrls.map(
      (url, index) => `Image ${index + 1}: ${url}`,
    );
    return `${baseComment}\n\n${imageLines.join('\n')}`;
  };

  const createFarmNoteWithImage = async (
    comment: string,
    extra?: {lat?: number | null; long?: number | null},
  ) => {
    const imageUrls = await uploadNoteImages();
    const input = {
      noteTypeId: 1,
      farmId: farmId!,
      farmFieldId: farmFieldId,
      comment: buildCommentWithImageUrls(comment, imageUrls),
      lat: extra?.lat ?? null,
      long: extra?.long ?? null,
    };

    return apiCreateFarmNote(input, farmFieldId, farmId!);
  };

  const handleSaveGeneralNote = async () => {
    if (!canSaveGeneralNotes) return;
    setIsLoading(true);
    try {
      await createFarmNoteWithImage(`${noteTitle}\n\n${notes}`, {
        lat: null,
        long: null,
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(error);
      alert('Failed to save note.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSoilWithNotes = async () => {
    if (!soilData) return;
    setIsLoading(true);
    try {
      const noteComment =
        noteTitle.trim() || notes.trim()
          ? `${noteTitle}\n\n${notes}`.trim()
          : `Soil Assessment - ${new Date().toISOString().split('T')[0]}`;

      const noteRes = await createFarmNoteWithImage(noteComment);
      const noteId = noteRes?.id;

      if (!noteId) {
        throw new Error('Failed to create note for Soil Assessment');
      }

      // 2. Create/Update Soil Assessment
      const soilInput = {
        farmId: farmId!,
        farmFieldId: farmFieldId,
        noteId: noteId,
        soilType: soilData.soilType,
        pH: Number(soilData.pH) || undefined,
        P: Number(soilData.P) || undefined,
        K: Number(soilData.K) || undefined,
        Ca: Number(soilData.Ca) || undefined,
        Mg: Number(soilData.Mg) || undefined,
        Na: Number(soilData.Na) || undefined,
        totalCations: Number(soilData.cations) || undefined,
        exchangeableAcidity: Number(soilData.exchangeableAcidity) || undefined,
        Zn: Number(soilData.Zn) || undefined,
        Mn: Number(soilData.Mn) || undefined,
        Cu: Number(soilData.Cu) || undefined,
      };

      await apiCreateSoilAssessment(soilInput);

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(error);
      alert('Failed to save soil data.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveIssueLogWithNotes = async () => {
    if (!canSaveIssueLog) return;
    setIsLoading(true);
    try {
      const noteComment =
        noteTitle.trim() || notes.trim()
          ? `${noteTitle}\n\n${notes}`.trim()
          : `Issue Log: ${issueLogType} - ${issueLogDate}`;

      const noteRes = await createFarmNoteWithImage(noteComment);
      const noteId = noteRes?.id;

      if (!noteId) {
        throw new Error('Failed to create note for Issue Log');
      }

      // 2. Create/Update Issue Log
      const baseIssueLogInput = {
        farmId: farmId!,
        farmFieldId: farmFieldId,
        noteId: noteId,
        issueDate: issueLogDate,
        issueType: issueLogType,
        severityScale: Number(issueLogSeverityScale) || 1,
        controlMeasuresApplied: issueLogControlMeasures,
        preventativeMeasuresTaken: issueLogPreventativeMeasures,
        issueDuringHarvest: issueLogDuringHarvest,
      };

      const selectedPestDiseaseId = Number(issueLogPestsAndDiseasesId) || null;
      const issueLogInputVariants = selectedPestDiseaseId
        ? [
            {
              ...baseIssueLogInput,
              pestsAndDiseasesId: selectedPestDiseaseId,
            },
            {
              ...baseIssueLogInput,
              pestDiseaseId: selectedPestDiseaseId,
            },
          ]
        : [baseIssueLogInput];

      const executeWithFallback = async (
        run: (input: any) => Promise<any>,
      ): Promise<any> => {
        let lastError: any = null;
        for (const inputVariant of issueLogInputVariants) {
          try {
            return await run(inputVariant);
          } catch (error: any) {
            lastError = error;
            const message = String(
              error?.message || error?.errors?.[0]?.message || '',
            );
            const isUnknownFieldError =
              message.includes('not defined by type "IssueLogInput"') ||
              message.includes('Field "pestsAndDiseasesId"') ||
              message.includes('Field "pestDiseaseId"');
            if (!isUnknownFieldError) {
              throw error;
            }
          }
        }
        throw lastError || new Error('Failed to save issue log.');
      };

      if (existingIssueLogId) {
        await executeWithFallback((input) =>
          apiUpdateIssueLog(existingIssueLogId, input),
        );
      } else {
        const res = await executeWithFallback((input) =>
          apiCreateIssueLog(input),
        );
        if (res?.id) setExistingIssueLogId(Number(res.id));
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(error);
      alert('Failed to save issue log.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveGrowthObservationWithNotes = async () => {
    const stageId = growthObservationStageIdMap[growthObservationStage];
    if (!stageId) {
      alert('Please select a valid growth observation stage.');
      return;
    }

    setIsLoading(true);
    try {
      const extraLines: string[] = [];
      const latestAcceptablePlantingDate =
        growthObservationData.latestAcceptablePlantingDate?.trim();
      if (latestAcceptablePlantingDate) {
        extraLines.push(
          `Latest acceptable planting date: ${latestAcceptablePlantingDate}`,
        );
      }

      // GrowthObservationInput does NOT accept latestAcceptablePlantingDate
      // (not in ERD table and gateway rejects it), so strip it out here.
      const {
        latestAcceptablePlantingDate: _ignoredLatestAcceptablePlantingDate,
        observationDate,
        expectedGerminationRate,
        estimatedYield,
        plannedHa,
        haPrepared,
        rowSpacing,
        plantedPct,
        plantHeightCm,
        germinationRatePct,
        seedlingVigorScale,
        rootLengthScale,
        cropMaturityAssessment,
        uniformityOfMaturity,
        leavesPerPlant,
        floweringUniformityScale,
        marketReady,
        marketReadyComment,
        fruitSetPercent,
        fruitSetUniformityScale,
        fruitAvgLengthCm,
        fruitAvgDiameterCm,
        pctCropReadyForHarvest,
        estimatedHarvestDate,
        logisticsReady,
        comment: growthObservationComment,
      } = growthObservationData;
      void _ignoredLatestAcceptablePlantingDate;

      const toNum = (v: unknown) => {
        if (v === null || v === undefined) return undefined;
        const s = String(v).trim();
        if (!s) return undefined;
        const n = Number(s);
        return Number.isFinite(n) ? n : undefined;
      };

      // Backend GrowthObservationInput does not currently accept these fields,
      // but we still want to preserve user input.
      const haPreparedNum = toNum(haPrepared);
      if (haPreparedNum !== undefined) {
        extraLines.push(`Ha prepared: ${haPreparedNum}`);
      }
      const rowSpacingNum = toNum(rowSpacing);
      if (rowSpacingNum !== undefined) {
        extraLines.push(`Row spacing: ${rowSpacingNum}`);
      }
      const plantedPctNum = toNum(plantedPct);
      if (plantedPctNum !== undefined) {
        extraLines.push(`Planted %: ${plantedPctNum}`);
      }

      const growthObsComment = (growthObservationComment || '').trim();
      if (growthObsComment) {
        extraLines.push(`Observation comment: ${growthObsComment}`);
      }

      const estimatedHarvestDateTrimmed = (estimatedHarvestDate || '').trim();
      if (estimatedHarvestDateTrimmed) {
        extraLines.push(
          `Estimated harvest date: ${estimatedHarvestDateTrimmed}`,
        );
      }
      if (logisticsReady !== undefined) {
        extraLines.push(`Logistics ready: ${logisticsReady ? 'Yes' : 'No'}`);
      }
      if (marketReady !== undefined) {
        extraLines.push(`Market ready: ${marketReady ? 'Yes' : 'No'}`);
      }
      const marketReadyCommentTrimmed = (marketReadyComment || '').trim();
      if (marketReadyCommentTrimmed) {
        extraLines.push(`Market ready comment: ${marketReadyCommentTrimmed}`);
      }

      // Backend GrowthObservationInput does not currently accept these fields,
      // but we still want to preserve user input.
      const tonsHedgedNum = toNum(growthObservationData.tonsHedged);
      if (tonsHedgedNum !== undefined) {
        extraLines.push(`Tons hedged: ${tonsHedgedNum}`);
      }
      const fruitAvgDiameterCmNum = toNum(fruitAvgDiameterCm);
      if (fruitAvgDiameterCmNum !== undefined) {
        extraLines.push(`Fruit avg diameter (cm): ${fruitAvgDiameterCmNum}`);
      }

      const noteCommentBase =
        noteTitle.trim() || notes.trim()
          ? `${noteTitle}\n\n${notes}`.trim()
          : `Growth Observation: ${growthObservationStage} - ${growthObservationData.observationDate}`;
      const noteComment = [noteCommentBase, ...extraLines].join('\n');

      const noteRes = await createFarmNoteWithImage(noteComment);
      const noteId = noteRes?.id;

      if (!noteId) {
        throw new Error('Failed to create note for Growth Observation');
      }

      const observationInput = {
        farmId: farmId!,
        farmFieldId: farmFieldId,
        noteId: noteId,
        stageId,
        // Dates/strings/bools (explicit allowlist to avoid gateway rejects)
        observationDate,
        // Floats/ints expected by backend
        expectedGerminationRate: toNum(expectedGerminationRate),
        estimatedYield: toNum(estimatedYield),
        plannedHa: toNum(plannedHa),
        plantHeightCm: toNum(plantHeightCm),
        germinationRatePct: toNum(germinationRatePct),
        seedlingVigorScale: toNum(seedlingVigorScale),
        rootLengthScale: toNum(rootLengthScale),
        cropMaturityAssessment: toNum(cropMaturityAssessment),
        uniformityOfMaturity: toNum(uniformityOfMaturity),
        leavesPerPlant: toNum(leavesPerPlant),
        floweringUniformityScale: toNum(floweringUniformityScale),
        fruitSetPercent: toNum(fruitSetPercent),
        fruitSetUniformityScale: toNum(fruitSetUniformityScale),
        fruitAvgLengthCm: toNum(fruitAvgLengthCm),
        pctCropReadyForHarvest: toNum(pctCropReadyForHarvest),
      };

      await apiCreateGrowthObservation(observationInput);

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(error);
      alert('Failed to save growth observation.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveManagementLogWithNotes = async () => {
    if (!managementLogData.logDate?.trim()) return;
    setIsLoading(true);
    try {
      const noteComment =
        noteTitle.trim() || notes.trim()
          ? `${noteTitle}\n\n${notes}`.trim()
          : `Management Log - ${managementLogData.logDate}`;

      const noteRes = await createFarmNoteWithImage(noteComment);
      const noteId = noteRes?.id;

      if (!noteId) {
        throw new Error('Failed to create note for Management Log');
      }

      // Backend schema requires several string fields to be non-null,
      // so ensure they are always provided (at least as empty strings).
      await apiCreateManagementLog({
        farmId: farmId!,
        farmFieldId: farmFieldId,
        noteId: noteId,
        logDate: managementLogData.logDate,
        activityType: managementLogData.activityType ?? '',
        fertilizerType: managementLogData.fertilizerType ?? '',
        irrigationFrequency: managementLogData.irrigationFrequency ?? '',
        waterStressSigns: managementLogData.waterStressSigns ?? '',
        initialTreatmentActions:
          managementLogData.initialTreatmentActions ?? '',
        applicationRateKgha: Number(managementLogData.applicationRateKgha) || 0,
        irrigationVolume: Number(managementLogData.irrigationVolume) || 0,
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(error);
      alert('Failed to save management log.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveHarvestSummaryWithNotes = async () => {
    const storageConditions = (
      harvestSummaryData.storageConditions || ''
    ).trim();
    const harvestMethod = (harvestSummaryData.harvestMethod || '').trim();
    if (!storageConditions) {
      alert('Storage conditions are required to save harvest summary.');
      return;
    }
    if (!harvestMethod) {
      alert('Harvest method is required to save harvest summary.');
      return;
    }

    setIsLoading(true);
    try {
      const noteComment =
        noteTitle.trim() || notes.trim()
          ? `${noteTitle}\n\n${notes}`.trim()
          : `Harvest Summary - ${new Date().toISOString().split('T')[0]}`;

      const noteRes = await createFarmNoteWithImage(noteComment);
      const noteId = noteRes?.id;

      if (!noteId) {
        throw new Error('Failed to create note for Harvest Summary');
      }

      await apiCreateHarvestSummary({
        farmId: farmId!,
        farmFieldId: farmFieldId,
        noteId: noteId,
        // Backend requires these string fields to be non-null
        harvestMethod,
        storageConditions,
        totalHarvestedQuantityKg:
          Number(harvestSummaryData.totalHarvestedQuantityKg) || 0,
        avgYieldPerHectareKgha:
          Number(harvestSummaryData.avgYieldPerHectareKgha) || 0,
        pctFirstGradeProduce:
          Number(harvestSummaryData.pctFirstGradeProduce) || 0,
        harvestEfficiencyScale:
          Number(harvestSummaryData.harvestEfficiencyScale) || 0,
        laborersUsed: Number(harvestSummaryData.laborersUsed) || 0,
        // Optional text fields – send empty strings when not provided
        commonQualityIssues: harvestSummaryData.commonQualityIssues ?? '',
        equipmentUsed: harvestSummaryData.equipmentUsed ?? '',
        initialProcessingSteps: harvestSummaryData.initialProcessingSteps ?? '',
        transportationArrangements:
          harvestSummaryData.transportationArrangements ?? '',
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(error);
      alert('Failed to save harvest summary.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSavePostHarvestReviewWithNotes = async () => {
    setIsLoading(true);
    try {
      const noteComment =
        noteTitle.trim() || notes.trim()
          ? `${noteTitle}\n\n${notes}`.trim()
          : `Post Harvest Review - ${new Date().toISOString().split('T')[0]}`;

      const noteRes = await createFarmNoteWithImage(noteComment);
      const noteId = noteRes?.id;

      if (!noteId) {
        throw new Error('Failed to create note for Post Harvest Review');
      }

      await apiCreatePostHarvestReview({
        ...postHarvestReviewData,
        farmId: farmId!,
        farmFieldId: farmFieldId,
        noteId: noteId,
      });

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(error);
      alert('Failed to save post harvest review.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveCustomerSupport = async () => {
    setIsLoading(true);
    try {
      const noteComment = `[SUPPORT] ${
        customerSupportData.subject
      } (${customerSupportData.priority.toUpperCase()})\n\n${
        customerSupportData.description
      }`;

      const input = {
        noteTypeId: 8, // Customer Support Note
        farmId: farmId!,
        farmFieldId: farmFieldId,
        comment: noteComment,
      };

      await apiCreateFarmNote(input, farmFieldId, farmId!);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error(error);
      alert('Failed to save support request.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageFiles = (files: FileList | null) => {
    if (!files?.length) return;
    const imageFiles = Array.from(files).filter((f) =>
      ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(f.type),
    );
    setUploadedImages((prev) => [...prev, ...imageFiles]);
  };

  // ... rest of helpers ...
  const handleUploadSave = () => {
    setShowUploadModal(false);
  };

  const handleUploadCancel = () => {
    setShowUploadModal(false);
    setUploadedImages([]);
  };

  return (
    <Box
      p={3}
      borderRadius={12}
      boxShadow={1}
      bgcolor="#FAFAFA"
      maxWidth={500}
      margin="auto"
      style={{
        maxHeight: '100%',
        overflowY: 'auto',
        position: 'relative',
      }}
    >
      {isLoading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(255,255,255,0.7)"
          zIndex={10}
        >
          <CircularProgress />
        </Box>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h6" style={{fontWeight: 600}}>
          Add New Data Point for{' '}
          {selectedField.id === 'GENERAL'
            ? 'General Farm'
            : selectedField.info?.fieldName || 'Field'}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DataPointTypeSelector
        value={dataPointType}
        onChange={setDataPointType}
        disabledTypes={[]}
      />

      {fieldOptions.length > 0 && (
        <Box mt={2}>
          <Typography style={{fontSize: 14, fontWeight: 500, marginBottom: 8}}>
            Field
          </Typography>
          <TextField
            select
            fullWidth
            variant="outlined"
            size="small"
            value={selectedFieldId}
            onChange={(e) => setSelectedFieldId(e.target.value)}
            InputProps={{style: {backgroundColor: '#fff', borderRadius: 8}}}
          >
            {fieldOptions.map((f) => {
              const id = String(f.id);
              return (
                <MenuItem key={id} value={id}>
                  {formatFarmFieldOptionLabel(f)}
                </MenuItem>
              );
            })}
          </TextField>
        </Box>
      )}

      {dataPointType === EDataPointType.GENERAL_NOTES && (
        <Box mt={2}>
          <GeneralNoteForm
            location={location}
            noteTitle={noteTitle}
            notes={notes}
            uploadedImages={uploadedImages}
            onLocationChange={setLocation}
            onNoteTitleChange={setNoteTitle}
            onNotesChange={setNotes}
            onImageUploadClick={() => setShowUploadModal(true)}
            onChooseOnMap={onChooseOnMap}
          />
          <Box mt={3} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              style={{
                borderRadius: 8,
                backgroundColor: canSaveGeneralNotes ? '#2D9B73' : '#ccc',
                color: '#fff',
                fontWeight: 600,
              }}
              disabled={!canSaveGeneralNotes}
              onClick={handleSaveGeneralNote}
            >
              Save
            </Button>
          </Box>
        </Box>
      )}

      {dataPointType === 'Issue Log' && (
        <Box mt={2}>
          <Typography style={{fontSize: 14, fontWeight: 500, marginBottom: 12}}>
            Issue Log
          </Typography>
          <IssueLogForm
            issueDate={issueLogDate}
            issueType={issueLogType}
            pestsAndDiseasesId={issueLogPestsAndDiseasesId}
            pestDiseaseOptions={pestDiseaseOptions}
            pestDiseaseOptionsLoading={pestDiseaseOptionsLoading}
            pestDiseaseOptionsError={pestDiseaseOptionsError}
            severityScale={issueLogSeverityScale}
            controlMeasures={issueLogControlMeasures}
            preventativeMeasures={issueLogPreventativeMeasures}
            duringHarvest={issueLogDuringHarvest}
            onIssueDateChange={setIssueLogDate}
            onIssueTypeChange={setIssueLogType}
            onPestsAndDiseasesIdChange={setIssueLogPestsAndDiseasesId}
            onSeverityScaleChange={setIssueLogSeverityScale}
            onControlMeasuresChange={setIssueLogControlMeasures}
            onPreventativeMeasuresChange={setIssueLogPreventativeMeasures}
            onDuringHarvestChange={setIssueLogDuringHarvest}
          />
          <Box mt={3}>
            <Typography
              style={{fontSize: 14, fontWeight: 500, marginBottom: 12}}
            >
              Add more details (optional)
            </Typography>
            <GeneralNoteForm
              location={location}
              noteTitle={noteTitle}
              notes={notes}
              uploadedImages={uploadedImages}
              onLocationChange={setLocation}
              onNoteTitleChange={setNoteTitle}
              onNotesChange={setNotes}
              onImageUploadClick={() => setShowUploadModal(true)}
              onChooseOnMap={onChooseOnMap}
            />
          </Box>
          <Box mt={3} display="flex" justifyContent="space-between">
            <Button
              onClick={onClose}
              variant="outlined"
              style={{
                borderRadius: 24,
                padding: '6px 18px',
                borderColor: '#ccc',
                backgroundColor: '#f9f9f9',
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              style={{
                borderRadius: 8,
                backgroundColor: canSaveIssueLog ? '#2D9B73' : '#ccc',
                color: '#fff',
                fontWeight: 600,
              }}
              disabled={!canSaveIssueLog}
              onClick={handleSaveIssueLogWithNotes}
            >
              Save
            </Button>
          </Box>
        </Box>
      )}

      {dataPointType === 'Soil Data' && (
        <Box mt={2}>
          <AddNewFieldSoilData
            embedded
            hidePrimaryAction
            initialSoilData={soilData || undefined}
            onSubmit={() => {}}
            onValuesChange={setSoilData}
          />
          <Box mt={3}>
            <Typography
              style={{fontSize: 14, fontWeight: 500, marginBottom: 12}}
            >
              Add more details (optional)
            </Typography>
            <GeneralNoteForm
              location={location}
              noteTitle={noteTitle}
              notes={notes}
              uploadedImages={uploadedImages}
              onLocationChange={setLocation}
              onNoteTitleChange={setNoteTitle}
              onNotesChange={setNotes}
              onImageUploadClick={() => setShowUploadModal(true)}
              onChooseOnMap={onChooseOnMap}
            />
          </Box>
          <Box mt={3} display="flex" justifyContent="space-between">
            <Button
              onClick={onClose}
              variant="outlined"
              style={{
                borderRadius: 24,
                padding: '6px 18px',
                borderColor: '#ccc',
                backgroundColor: '#f9f9f9',
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              style={{
                borderRadius: 8,
                backgroundColor: '#2D9B73',
                color: '#fff',
                fontWeight: 600,
              }}
              onClick={handleSaveSoilWithNotes}
            >
              Save
            </Button>
          </Box>
        </Box>
      )}

      {dataPointType === EDataPointType.CUSTOMER_SUPPORT && (
        <Box mt={2}>
          <CustomerSupportForm
            data={customerSupportData}
            onDataChange={setCustomerSupportData}
          />
          <Box mt={3} display="flex" justifyContent="space-between">
            <Button
              onClick={onClose}
              variant="outlined"
              style={{
                borderRadius: 24,
                padding: '6px 18px',
                borderColor: '#ccc',
                backgroundColor: '#f9f9f9',
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              style={{
                borderRadius: 8,
                backgroundColor: canSaveCustomerSupport ? '#2D9B73' : '#ccc',
                color: '#fff',
                fontWeight: 600,
              }}
              disabled={!canSaveCustomerSupport}
              onClick={handleSaveCustomerSupport}
            >
              Save
            </Button>
          </Box>
        </Box>
      )}

      {dataPointType === EDataPointType.GROWTH_OBSERVATION && (
        <Box mt={2}>
          <GrowthObservationForm
            stage={growthObservationStage}
            onStageChange={setGrowthObservationStage}
            data={growthObservationData}
            onDataChange={setGrowthObservationData}
          />
          <Box mt={3}>
            <Typography
              style={{fontSize: 14, fontWeight: 500, marginBottom: 12}}
            >
              Add more details (optional)
            </Typography>
            <GeneralNoteForm
              location={location}
              noteTitle={noteTitle}
              notes={notes}
              uploadedImages={uploadedImages}
              onLocationChange={setLocation}
              onNoteTitleChange={setNoteTitle}
              onNotesChange={setNotes}
              onImageUploadClick={() => setShowUploadModal(true)}
              onChooseOnMap={onChooseOnMap}
            />
          </Box>
          <Box mt={3} display="flex" justifyContent="space-between">
            <Button
              onClick={onClose}
              variant="outlined"
              style={{
                borderRadius: 24,
                padding: '6px 18px',
                borderColor: '#ccc',
                backgroundColor: '#f9f9f9',
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              style={{
                borderRadius: 8,
                backgroundColor: growthObservationStage ? '#2D9B73' : '#ccc',
                color: '#fff',
                fontWeight: 600,
              }}
              disabled={!growthObservationStage}
              onClick={handleSaveGrowthObservationWithNotes}
            >
              Save
            </Button>
          </Box>
        </Box>
      )}

      {dataPointType === EDataPointType.MANAGEMENT_LOG && (
        <Box mt={2}>
          <ManagementLogForm
            data={managementLogData}
            onDataChange={setManagementLogData}
          />
          <Box mt={3}>
            <Typography
              style={{fontSize: 14, fontWeight: 500, marginBottom: 12}}
            >
              Add more details (optional)
            </Typography>
            <GeneralNoteForm
              location={location}
              noteTitle={noteTitle}
              notes={notes}
              uploadedImages={uploadedImages}
              onLocationChange={setLocation}
              onNoteTitleChange={setNoteTitle}
              onNotesChange={setNotes}
              onImageUploadClick={() => setShowUploadModal(true)}
              onChooseOnMap={onChooseOnMap}
            />
          </Box>
          <Box mt={3} display="flex" justifyContent="space-between">
            <Button
              onClick={onClose}
              variant="outlined"
              style={{
                borderRadius: 24,
                padding: '6px 18px',
                borderColor: '#ccc',
                backgroundColor: '#f9f9f9',
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              style={{
                borderRadius: 8,
                backgroundColor: managementLogData.logDate ? '#2D9B73' : '#ccc',
                color: '#fff',
                fontWeight: 600,
              }}
              disabled={!managementLogData.logDate}
              onClick={handleSaveManagementLogWithNotes}
            >
              Save
            </Button>
          </Box>
        </Box>
      )}

      {dataPointType === EDataPointType.HARVEST_SUMMARY && (
        <Box mt={2}>
          <HarvestSummaryForm
            data={harvestSummaryData}
            onDataChange={setHarvestSummaryData}
          />
          <Box mt={3}>
            <Typography
              style={{fontSize: 14, fontWeight: 500, marginBottom: 12}}
            >
              Add more details (optional)
            </Typography>
            <GeneralNoteForm
              location={location}
              noteTitle={noteTitle}
              notes={notes}
              uploadedImages={uploadedImages}
              onLocationChange={setLocation}
              onNoteTitleChange={setNoteTitle}
              onNotesChange={setNotes}
              onImageUploadClick={() => setShowUploadModal(true)}
              onChooseOnMap={onChooseOnMap}
            />
          </Box>
          <Box mt={3} display="flex" justifyContent="space-between">
            <Button
              onClick={onClose}
              variant="outlined"
              style={{
                borderRadius: 24,
                padding: '6px 18px',
                borderColor: '#ccc',
                backgroundColor: '#f9f9f9',
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              style={{
                borderRadius: 8,
                backgroundColor: '#2D9B73',
                color: '#fff',
                fontWeight: 600,
              }}
              onClick={handleSaveHarvestSummaryWithNotes}
            >
              Save
            </Button>
          </Box>
        </Box>
      )}

      {dataPointType === EDataPointType.POST_HARVEST_REVIEW && (
        <Box mt={2}>
          <PostHarvestReviewForm
            data={postHarvestReviewData}
            onDataChange={setPostHarvestReviewData}
          />
          <Box mt={3}>
            <Typography
              style={{fontSize: 14, fontWeight: 500, marginBottom: 12}}
            >
              Add more details (optional)
            </Typography>
            <GeneralNoteForm
              location={location}
              noteTitle={noteTitle}
              notes={notes}
              uploadedImages={uploadedImages}
              onLocationChange={setLocation}
              onNoteTitleChange={setNoteTitle}
              onNotesChange={setNotes}
              onImageUploadClick={() => setShowUploadModal(true)}
              onChooseOnMap={onChooseOnMap}
            />
          </Box>
          <Box mt={3} display="flex" justifyContent="space-between">
            <Button
              onClick={onClose}
              variant="outlined"
              style={{
                borderRadius: 24,
                padding: '6px 18px',
                borderColor: '#ccc',
                backgroundColor: '#f9f9f9',
              }}
            >
              Back
            </Button>
            <Button
              variant="contained"
              style={{
                borderRadius: 8,
                backgroundColor: '#2D9B73',
                color: '#fff',
                fontWeight: 600,
              }}
              onClick={handleSavePostHarvestReviewWithNotes}
            >
              Save
            </Button>
          </Box>
        </Box>
      )}

      {dataPointType === '' && (
        <Typography
          variant="body2"
          color="textSecondary"
          style={{marginTop: 16}}
        >
          Select a data point type above to continue.
        </Typography>
      )}

      <ImageUploadModal
        open={showUploadModal}
        uploadedImages={uploadedImages}
        onClose={handleUploadCancel}
        onSave={handleUploadSave}
        onFilesSelected={handleImageFiles}
      />
    </Box>
  );
};
